import type { BookingTerms, LeaseTerms } from "../../../../../../types/bookings";
import { formatDateRange } from "../../../../../libs/utils";
import { formatCurrency } from "../../../../../libs/utils/formatter/formatCurrency";
import { BASE_RENT_ONLY } from "../../../../BookingForm/hooks/useTorRules";

export type ComparisonRowStatus = "changed" | "same";

export type TorRuleLike = { torPercent: number };

export type WhatsChangingRow = {
  key: string;
  label: string;
  expiring: string;
  renewal: string;
  renewalSuffix?: string;
  status: ComparisonRowStatus;
  isFooter?: boolean;
};

export type WhatsChangingComparisonInput = {
  expiringLeaseTerms?: LeaseTerms;
  renewalLeaseTerms?: LeaseTerms;
  expiringBookingTerms?: BookingTerms;
  renewalBookingTerms?: BookingTerms;
  expiringTorRules?: TorRuleLike[];
  expiringTorRuleCondition?: string;
  renewalTorRules?: TorRuleLike[];
  renewalTorRuleCondition?: string;
  expiringYear1Rent?: number;
  renewalYear1Rent?: number;
  torType?: string;
};

const normalizeCompareValue = (value: string) => value.trim().toLowerCase();

const isChanged = (expiring: string, renewal: string) =>
  normalizeCompareValue(expiring) !== normalizeCompareValue(renewal);

export const formatContractTerm = (numberOfTerms?: number, durationType?: string): string => {
  if (!numberOfTerms || !durationType) return "--";

  const unit = durationType.toLowerCase();
  if (numberOfTerms === 1) {
    const singular = unit.endsWith("s") ? unit.slice(0, -1) : unit;
    return `1 ${singular.charAt(0).toUpperCase() + singular.slice(1)}`;
  }

  return `${numberOfTerms} ${unit.charAt(0).toUpperCase() + unit.slice(1)}`;
};

export const formatTorPolicyLabel = (torRuleCondition?: string, torRules?: TorRuleLike[]): string => {
  if (!torRules?.length) return "--";

  const policyLabel = torRuleCondition === "and" ? "Tiered" : torRuleCondition === "or" ? "Flat" : "TOR";
  const percents = torRules.map((rule) => `${rule.torPercent}%`).join(" / ");

  return `${policyLabel} ${percents}`;
};

const formatTermDates = (leaseTerms?: LeaseTerms) =>
  formatDateRange(leaseTerms?.leaseStartDate, leaseTerms?.leaseEndDate);

const formatRent = (value?: number) => (value != null ? formatCurrency(value) : "--");

const formatContractValue = (value?: number) => (value != null ? formatCurrency(value) : "--");

const resolveLeaseAmount = (leaseTerms?: LeaseTerms, year1Rent?: number, bookingTerms?: BookingTerms) =>
  leaseTerms?.contractAmount ?? year1Rent ?? bookingTerms?.renewalAmount ?? bookingTerms?.annualBaseRent;

const resolveSecurityDeposit = (leaseTerms?: LeaseTerms, bookingTerms?: BookingTerms) => leaseTerms?.securityDeposit;

export const buildWhatsChangingRows = (input: WhatsChangingComparisonInput): WhatsChangingRow[] => {
  const {
    expiringLeaseTerms,
    renewalLeaseTerms,
    expiringBookingTerms,
    renewalBookingTerms,
    expiringTorRules,
    expiringTorRuleCondition,
    renewalTorRules,
    renewalTorRuleCondition,
    expiringYear1Rent,
    renewalYear1Rent,
    torType,
  } = input;

  const expiringContractTerm = formatContractTerm(
    expiringBookingTerms?.numberOfRenewalTerms,
    expiringBookingTerms?.durationType,
  );
  const renewalContractTerm = formatContractTerm(
    renewalBookingTerms?.numberOfRenewalTerms,
    renewalBookingTerms?.durationType,
  );

  const expiringTermDates = formatTermDates(expiringLeaseTerms);
  const renewalTermDates = formatTermDates(renewalLeaseTerms);

  const expiringAnnualRent = formatRent(
    resolveLeaseAmount(expiringLeaseTerms, expiringYear1Rent, expiringBookingTerms),
  );
  const renewalAnnualRent = formatRent(resolveLeaseAmount(renewalLeaseTerms, renewalYear1Rent, renewalBookingTerms));

  const expiringTorPolicy = formatTorPolicyLabel(expiringTorRuleCondition, expiringTorRules);
  const renewalTorPolicy = formatTorPolicyLabel(renewalTorRuleCondition, renewalTorRules);
  const torPolicyChanged = isChanged(expiringTorPolicy, renewalTorPolicy);

  const expiringDeposit = formatContractValue(resolveSecurityDeposit(expiringLeaseTerms, expiringBookingTerms));
  const renewalDeposit = formatContractValue(resolveSecurityDeposit(renewalLeaseTerms, renewalBookingTerms));

  const expiringContractValue = formatContractValue(
    resolveLeaseAmount(expiringLeaseTerms, expiringYear1Rent, expiringBookingTerms),
  );
  const renewalContractValue = formatContractValue(
    resolveLeaseAmount(renewalLeaseTerms, renewalYear1Rent, renewalBookingTerms),
  );

  const rows: WhatsChangingRow[] = [
    {
      key: "contract-term",
      label: "Contract Term",
      expiring: expiringContractTerm,
      renewal: renewalContractTerm,
      status: isChanged(expiringContractTerm, renewalContractTerm) ? "changed" : "same",
    },
    {
      key: "term-dates",
      label: "Term Dates",
      expiring: expiringTermDates,
      renewal: renewalTermDates,
      status: isChanged(expiringTermDates, renewalTermDates) ? "changed" : "same",
    },
    {
      key: "annual-rent-year-1",
      label: "Annual Rent (Year 1)",
      expiring: expiringAnnualRent,
      renewal: renewalAnnualRent,
      status: isChanged(expiringAnnualRent, renewalAnnualRent) ? "changed" : "same",
    },
  ];

  if (torType !== BASE_RENT_ONLY) {
    rows.push({
      key: "tor-policy",
      label: "TOR Policy",
      expiring: expiringTorPolicy,
      renewal: renewalTorPolicy,
      renewalSuffix: torPolicyChanged ? undefined : "(carried)",
      status: torPolicyChanged ? "changed" : "same",
    });
  }

  rows.push(
    {
      key: "security-deposit",
      label: "Security Deposit",
      expiring: expiringDeposit,
      renewal: renewalDeposit,
      status: isChanged(expiringDeposit, renewalDeposit) ? "changed" : "same",
    },
    {
      key: "total-contract-value",
      label: "Total Contract Value",
      expiring: expiringContractValue,
      renewal: renewalContractValue,
      status: isChanged(expiringContractValue, renewalContractValue) ? "changed" : "same",
      isFooter: true,
    },
  );

  return rows;
};
